import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const messagingService = {
  // Expose supabase for auth access in components
  supabase,
  
  // =====================================================
  // CONVERSATIONS
  // =====================================================
  
  async getConversations(userId) {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        buyer:buyer_id(id, full_name, email, avatar_url),
        supplier:suppliers!supplier_id(id, name_en, name_zh, logo_url, user_id),
        bean:beans(id, name_en, name_zh)
      `)?.or(`buyer_id.eq.${userId},supplier_id.in.(select id from suppliers where user_id = ${userId})`)?.eq('is_archived', false)?.order('last_message_at', { ascending: false });
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async getConversationById(conversationId) {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        buyer:buyer_id(id, full_name, email, avatar_url),
        supplier:suppliers!supplier_id(id, name_en, name_zh, logo_url, user_id),
        bean:beans(id, name_en, name_zh)
      `)?.eq('id', conversationId)?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async createConversation(conversationData) {
    const snakeData = toSnakeCase(conversationData);
    const { data, error } = await supabase?.from('conversations')?.insert(snakeData)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async archiveConversation(conversationId) {
    const { data, error } = await supabase?.from('conversations')?.update({ is_archived: true })?.eq('id', conversationId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  // =====================================================
  // MESSAGES
  // =====================================================
  
  async getMessages(conversationId, limit = 50) {
    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:sender_id(id, full_name, avatar_url)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true })?.limit(limit);
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async sendMessage(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const snakeData = toSnakeCase({
      ...messageData,
      senderId: user?.id,
      status: 'sent'
    });
    
    const { data, error } = await supabase?.from('messages')?.insert(snakeData)?.select(`
        *,
        sender:sender_id(id, full_name, avatar_url)
      `)?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async updateMessageStatus(messageId, status) {
    const { data, error } = await supabase?.from('messages')?.update({ status })?.eq('id', messageId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async markMessagesAsRead(conversationId, userId) {
    // Update all unread messages in conversation to 'read'
    const { error: messagesError } = await supabase?.from('messages')?.update({ status: 'read' })?.eq('conversation_id', conversationId)?.neq('sender_id', userId)?.in('status', ['sent', 'delivered']);
    
    if (messagesError) throw messagesError;
    
    // Reset unread count
    const { error: countError } = await supabase?.from('unread_message_counts')?.update({ unread_count: 0, updated_at: new Date()?.toISOString() })?.eq('conversation_id', conversationId)?.eq('user_id', userId);
    
    if (countError) throw countError;
  },
  
  // =====================================================
  // TYPING INDICATORS
  // =====================================================
  
  async setTypingStatus(conversationId, isTyping) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    if (isTyping) {
      const { error } = await supabase?.from('typing_indicators')?.upsert({
          conversation_id: conversationId,
          user_id: user?.id,
          is_typing: true,
          updated_at: new Date()?.toISOString()
        });
      
      if (error) throw error;
    } else {
      const { error } = await supabase?.from('typing_indicators')?.delete()?.eq('conversation_id', conversationId)?.eq('user_id', user?.id);
      
      if (error) throw error;
    }
  },
  
  async getTypingUsers(conversationId) {
    const { data, error } = await supabase?.from('typing_indicators')?.select(`
        *,
        user:user_id(id, full_name)
      `)?.eq('conversation_id', conversationId)?.eq('is_typing', true)?.gte('updated_at', new Date(Date.now() - 10000)?.toISOString()); // Last 10 seconds
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  // =====================================================
  // ONLINE STATUS
  // =====================================================
  
  async updateOnlineStatus(status) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { error } = await supabase?.from('user_online_status')?.upsert({
        id: user?.id,
        status,
        last_seen: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      });
    
    if (error) throw error;
  },
  
  async getUserOnlineStatus(userId) {
    const { data, error } = await supabase?.from('user_online_status')?.select('*')?.eq('id', userId)?.single();
    
    if (error && error?.code !== 'PGRST116') throw error; // Ignore not found
    return toCamelCase(data) || { status: 'offline' };
  },
  
  // =====================================================
  // UNREAD COUNTS
  // =====================================================
  
  async getUnreadCounts(userId) {
    const { data, error } = await supabase?.from('unread_message_counts')?.select('*')?.eq('user_id', userId);
    
    if (error) throw error;
    return toCamelCase(data);
  },
  
  async getTotalUnreadCount(userId) {
    const { data, error } = await supabase?.from('unread_message_counts')?.select('unread_count')?.eq('user_id', userId);
    
    if (error) throw error;
    
    const total = data?.reduce((sum, item) => sum + (item?.unread_count || 0), 0) || 0;
    return total;
  },
  
  // =====================================================
  // REAL-TIME SUBSCRIPTIONS
  // =====================================================
  
  subscribeToConversation(conversationId, callbacks) {
    const channel = supabase?.channel(`conversation:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          if (callbacks?.onNewMessage) {
            callbacks?.onNewMessage(toCamelCase(payload?.new));
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          if (callbacks?.onMessageUpdate) {
            callbacks?.onMessageUpdate(toCamelCase(payload?.new));
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          if (callbacks?.onTypingChange) {
            callbacks?.onTypingChange(toCamelCase(payload));
          }
        }
      )?.subscribe();
    
    return channel;
  },
  
  subscribeToUserConversations(userId, callbacks) {
    const channel = supabase?.channel(`user_conversations:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          if (callbacks?.onConversationUpdate) {
            callbacks?.onConversationUpdate(toCamelCase(payload));
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'unread_message_counts',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          if (callbacks?.onUnreadCountChange) {
            callbacks?.onUnreadCountChange(toCamelCase(payload));
          }
        }
      )?.subscribe();
    
    return channel;
  },
  
  subscribeToOnlineStatus(userIds, callback) {
    const channel = supabase?.channel('online_status')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_online_status'
        },
        (payload) => {
          if (callback) {
            callback(toCamelCase(payload));
          }
        }
      )?.subscribe();
    
    return channel;
  },
  
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};